import React, { useEffect, useMemo, useState } from 'react';
import '../styles/Filmography.css';
import { useTranslation } from 'react-i18next';
import { HiOutlinePlay, HiOutlinePhotograph } from "react-icons/hi";
import { Helmet } from 'react-helmet-async';

const API_BASE = process.env.REACT_APP_API_BASE || "";

function extractYearKey(yearStr) {
  const s = String(yearStr || '').trim();
  if (!s) return 'ETC';
  const m = s.match(/\d{4}/);
  return m ? m[0] : 'ETC';
}

function yearSortDesc(a, b) {
  if (a === 'ETC' && b !== 'ETC') return 1;
  if (b === 'ETC' && a !== 'ETC') return -1;
  return String(b).localeCompare(String(a), 'en');
}

export default function Filmography() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState('');
  const [groupBy, setGroupBy] = useState('category');
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'classic');
  const [selectedMedia, setSelectedMedia] = useState(null); 
  const { t, i18n } = useTranslation();

  const getLangData = (item, field) => {
    const lang = (i18n.language || 'KR').toLowerCase();
    const key = `${field}_${lang}`;
    return item[key] || item[`${field}_kr`] || item[field];
  };

  // 🎵 영상 모달 상태에 따라 BGM 제어 이벤트 발생
  useEffect(() => {
    if (selectedMedia && selectedMedia.media_type === 'VIDEO') {
      window.dispatchEvent(new Event('videoPlaying'));
    } else {
      window.dispatchEvent(new Event('videoPaused'));
    }
    return () => window.dispatchEvent(new Event('videoPaused'));
  }, [selectedMedia]);

  useEffect(() => {
    const onTheme = () => setTheme(localStorage.getItem('theme') || 'classic');
    window.addEventListener('themechange', onTheme);
    return () => window.removeEventListener('themechange', onTheme);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setErr('');
        const res = await fetch(`${API_BASE}/api/filmography_list.php`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Load failed');
        setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        setErr(e.message);
      }
    })();
  }, []);

  const grouped = useMemo(() => {
    const GENRE_ORDER = ['DRAMA', 'FEATURE FILM', 'SHORT FILM', 'PLAY', 'COMMERCIALS', 'MUSIC VIDEO', 'AWARDS'];
    const map = {};
    items.forEach(item => {
      const key = groupBy === 'year' ? extractYearKey(item.year) : (item.category || 'DRAMA');
      if (!map[key]) map[key] = [];
      map[key].push(item);
    });

    return Object.entries(map).sort(([a], [b]) => {
      if (groupBy === 'year') return yearSortDesc(a, b);
      const idxA = GENRE_ORDER.indexOf(a);
      const idxB = GENRE_ORDER.indexOf(b);
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    });
  }, [items, groupBy]);

  const getEmbedUrl = (url) => {
    if (!url) return "";

    const ytRegExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const ytMatch = url.match(ytRegExp);
    if (ytMatch && ytMatch[2].length === 11) {
      return `https://www.youtube.com/embed/${ytMatch[2]}?autoplay=0`;
    }

    const ttRegExp = /tiktok\.com\/@.*\/video\/(\d+)/;
    const ttMatch = url.match(ttRegExp);
    if (ttMatch) {
      return `https://www.tiktok.com/embed/v2/${ttMatch[1]}`;
    }

    return url;
  };

  return (
    <>
    <Helmet>
      <title>배우 한지우 필모그래피</title>
      <meta name="description" content="배우 한지우의 출연 영화 및 드라마 정보와 필모그래피 정보를 확인하세요." />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="배우 한지우 필모그래피" />
      <meta property="og:description" content="배우 한지우의 출연 영화 및 드라마 정보와 필모그래피 정보를 확인하세요." />
      <meta property="og:url" content="https://hanjiwoo.com/filmography" />
      <meta property="og:image" content="https://hanjiwoo.com/og/og-home.jpg" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="배우 한지우 필모그래피" />
      <meta name="twitter:description" content="배우 한지우의 출연 영화 및 드라마 정보와 필모그래피 정보를 확인하세요." />
      <meta name="twitter:image" content="https://hanjiwoo.com/og/og-home.jpg" />

      <link rel="canonical" href="https://hanjiwoo.com/filmography" />
    </Helmet>
    <div className="filmo-page">
      <h1 className="section-title">{t('nav_filmo')}</h1>
      <div className="filmo-head">
        <div className="head-titles">
          <span className="total-badge">{items.length} WORKS</span>
        </div>
        <div className="filmo-controls">
          <button className={`seg-btn ${groupBy === 'category' ? 'is-active' : ''}`} onClick={() => setGroupBy('category')}>{t('genre')}</button>
          <button className={`seg-btn ${groupBy === 'year' ? 'is-active' : ''}`} onClick={() => setGroupBy('year')}>{t('year')}</button>
        </div>
      </div>

      {err && <p className="err-msg">{err}</p>}

      <div className={`filmo-board ${theme}`}>
        {grouped.map(([cat, list]) => (
          <div key={cat} className="filmo-section">
            <div className="section-divider">
              <span className="divider-label">{cat}</span>
              <div className="divider-line"></div>
            </div>
            <ul className="filmo-list">
              {list.map(row => {
                const currentTitle = getLangData(row, 'title');
                const currentRole = getLangData(row, 'role');
                return (
                  <li 
                    key={row.id} 
                    className={`filmo-item ${row.media_type !== 'NONE' ? 'has-media' : ''}`}
                    onClick={() => row.media_type !== 'NONE' && setSelectedMedia({
                        ...row,
                        displayTitle: currentTitle,
                        displayRole: currentRole
                      })}
                    >
                    <span className="filmo-year">{row.year}</span>
                    <div className="filmo-content">
                      <h4 className="filmo-title">
                        {currentTitle}
                        <span className="icon-media-wrapper">
                          {row.media_type === 'VIDEO' && <HiOutlinePlay size={18} className="icon-line icon-video" />}
                          {row.media_type === 'IMAGE' && <HiOutlinePhotograph size={18} className="icon-line icon-image" />}
                        </span>
                      </h4>
                      <span className="filmo-role">{currentRole}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {selectedMedia && (
        <div className="media-modal" onClick={() => setSelectedMedia(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedMedia(null)}>×</button>
            <div className="modal-body">
              {selectedMedia.media_type === 'VIDEO' ? (
                <div className={`video-container ${selectedMedia.media_url.includes('tiktok') ? 'is-tiktok' : ''}`}>
                  <iframe src={getEmbedUrl(selectedMedia.media_url)} frameBorder="0" allow="autoplay; encrypted-media" allowFullScreen title="v" />
                </div>
              ) : (
                <img src={selectedMedia.media_url} alt="poster" className="modal-poster" />
              )}
              <div className="modal-info" onClick={() => setSelectedMedia(null)} style={{ cursor: 'pointer' }}>
                <h3>{selectedMedia.displayTitle}</h3>
                <p>{selectedMedia.year} · {selectedMedia.displayRole}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}